import { useCallback } from 'react'
import {
  Column,
  Content,
  ContentTable,
  TDocumentDefinitions
} from 'pdfmake/interfaces'
import {
  TDocumentSection,
  TFromSection,
  TToSection,
  TTotalTableData
} from './types'
import {
  DEFAULT_PDF_DOC_TXT,
  defaultA4DocValues,
  defaultFinancialsDocStyles,
  svgLogo,
  tableLayout
} from './constants'
import {
  TCognusARDocumentInvoiceData,
  TSummaryData
} from '../../Financials/CognusAR/CognusARTable/types'
import { generatePaginatedFooterUpdatedUI, generatePDFDoc } from '../helpers'
import { currencyFormatter } from '../../ContractDetails/ContractDetailsGeneral/ContractDetailsForm/utils'
import {
  TAdminDocumentCommonsRemittanceAdviceInvoiceData,
  TAdminDocumentCommunityInvoiceData,
  TAdminDocumentCommunityRemittanceAdviceInvoiceData,
  TAdminDocumentHSRemittanceAdviceInvoiceData,
  TGPOInvoiceData,
  TVendorInvoiceData
} from '../../Financials/Admin/AdminTable/types'
import { TOptionsWithValueLabelResponse } from '../../../constants'
import { TFinancialsDocumentInvoiceData } from '../../Financials/Rebates/RebatesTable/types'

export const useDocContentGeneration = () => {
  const getPdfFileName = useCallback(
    (name: string, id: string) => `${name} ${id}.pdf`,
    []
  )

  const generateFromInfo = useCallback(
    ({ name, address, id }: TFromSection) => ({
      width: '*',
      stack: [
        { text: DEFAULT_PDF_DOC_TXT.FROM, style: 'from-to-header doc-text' },
        {
          text: name,
          style: 'doc-text'
        },
        {
          text: address,
          style: 'doc-text'
        },
        {
          text: id ? `${DEFAULT_PDF_DOC_TXT.DEPARTMENT} #${id}` : '',
          style: 'doc-text'
        }
      ]
    }),
    []
  )

  const generateToInfo = useCallback(
    ({ name, address, phoneNumber }: TToSection) => ({
      width: '*',
      stack: [
        {
          text: DEFAULT_PDF_DOC_TXT.BILL_TO,
          style: 'from-to-header doc-text'
        },
        {
          text: name,
          style: 'doc-text'
        },
        {
          text: address,
          style: 'doc-text'
        },
        {
          text: phoneNumber,
          marginBottom: 15,
          style: 'doc-text'
        }
      ]
    }),
    []
  )

  const generateFromToSection = useCallback(
    (fromInfo: TFromSection, toInfo: TToSection): (Content | Column)[] => [
      {
        columns: [generateFromInfo(fromInfo), generateToInfo(toInfo)],
        columnGap: 24,
        margin: [0, 0, 0, 8]
      }
    ],
    [generateFromInfo, generateToInfo]
  )

  const generateDocumentInfo = useCallback(
    (documentInfo: TDocumentSection): (Content | Column)[] => [
      {
        columns: [
          { svg: svgLogo, width: 120, margin: [0, 0, 0, 48] },
          {
            width: '*',
            alignment: 'right',
            stack: [
              {
                text: documentInfo.docTitle?.toUpperCase() ?? '',
                fontSize: 20,
                bold: true,
                color: '#333',
                margin: [0, 0, 0, 4]
              },
              {
                text: `${documentInfo.docHeaderName.toUpperCase()} #${
                  documentInfo.docId
                }`,
                style: 'doc-info'
              },
              {
                text: `${DEFAULT_PDF_DOC_TXT.DATE}: ${documentInfo.docDate}`,
                style: 'doc-info'
              },
              {
                text: `${DEFAULT_PDF_DOC_TXT.DUE_ON}: ${documentInfo.docDueOn}`,
                style: 'doc-info'
              }
            ]
          }
        ],
        margin: [0, 0, 0, 12]
      }
    ],
    []
  )

  const generateCommentsSection = useCallback(
    (comments: string[]) => [
      {
        width: '*',
        stack: [
          { text: `${DEFAULT_PDF_DOC_TXT.REMARKS}:`, style: 'remarks-section' },
          ...comments.map((commentItem) => ({
            text: commentItem,
            style: 'doc-text'
          }))
        ]
      }
    ],
    []
  )

  const generateTotalTable = useCallback((data?: TTotalTableData) => {
    if (!data) return []
    return [
      {
        width: 304,
        table: {
          widths: ['*', 80],
          body: data.map((item) => [
            {
              alignment: 'right',
              text: item.label.toUpperCase()
            },
            {
              text: `$ ${currencyFormatter(item.value)}`,
              alignment: 'right'
            }
          ])
        },
        layout: {
          hLineColor: '#E0E0E0',
          vLineColor: '#E0E0E0',
          paddingLeft: () => 6,
          paddingRight: () => 6,
          paddingTop: () => 4,
          paddingBottom: () => 4
        }
      }
    ]
  }, [])

  const generateTotalDue = useCallback(
    (data?: TOptionsWithValueLabelResponse) => {
      if (!data) return []
      return [
        {
          width: 304,
          table: {
            widths: ['*', 80],
            body: [
              [
                {
                  alignment: 'right',
                  text: data.label.toUpperCase(),
                  style: 'blue-table'
                },
                {
                  text: `$ ${currencyFormatter(data.value)}`,
                  alignment: 'right',
                  style: 'blue-table'
                }
              ]
            ]
          },
          layout: {
            hLineColor: '#1A5099',
            vLineColor: '#1A5099',
            paddingLeft: () => 6,
            paddingRight: () => 6,
            paddingTop: () => 4,
            paddingBottom: () => 4
          }
        }
      ]
    },
    []
  )

  const generateCommentsAndTotalSection = useCallback(
    ({
      comments,
      data,
      totalDue
    }: {
      comments: string[]
      data?: TTotalTableData
      totalDue?: TOptionsWithValueLabelResponse
    }): (Content | Column)[] => [
      {
        columns: [{ width: '*', text: '' }, ...generateTotalDue(totalDue)],
        columnGap: 16,
        margin: [0, 0, 0, 6]
      },
      {
        columns: [
          ...generateCommentsSection(comments),
          ...generateTotalTable(data)
        ],
        columnGap: 16,
        margin: [0, 0, 0, 6]
      }
    ],
    [generateCommentsSection, generateTotalTable, generateTotalDue]
  )

  const generateSummaryTable = useCallback(
    (data: TSummaryData[]): Content[] => {
      const tableHeader = [
        data[0]?.health_system_name
          ? {
              text: DEFAULT_PDF_DOC_TXT.HEALTH_SYSTEM,
              style: 'tableHeader'
            }
          : null,
        data[0]?.hospital_name
          ? { text: DEFAULT_PDF_DOC_TXT.HOSPITAL, style: 'tableHeader' }
          : null,
        { text: DEFAULT_PDF_DOC_TXT.CONTRACT, style: 'tableHeader' },
        {
          text: DEFAULT_PDF_DOC_TXT.ADMIN_FEE,
          style: 'tableHeaderRightAlignment'
        }
      ].filter((item) => !!item)

      return [
        {
          text: data[0]?.health_system_name
            ? DEFAULT_PDF_DOC_TXT.SUMMARY_BY_HEALTH_SYSTEM
            : DEFAULT_PDF_DOC_TXT.SUMMARY_BY_HOSPITAL,
          style: 'table-title',
          pageBreak: 'before'
        },
        {
          table: {
            headerRows: 1,
            dontBreakRows: true,
            widths:
              tableHeader.length === 3
                ? [150, '*', 80]
                : tableHeader.length === 2
                ? ['*', 80]
                : [150, 120, '*', 80],
            body: [
              tableHeader.map((item) => ({
                ...item,
                text: item?.text.toUpperCase()
              })),
              ...data.map((item) => {
                const filteredItem: string[] = []
                if (item.health_system_name)
                  filteredItem.push(item.health_system_name)
                if (item.hospital_name) filteredItem.push(item.hospital_name)
                return [
                  ...filteredItem,
                  item.contract_number,
                  {
                    text: `$${currencyFormatter(item.admin_fee)}`,
                    alignment: 'right'
                  }
                ]
              })
            ]
          },
          layout: tableLayout,
          margin: [0, 6, 0, 10]
        }
      ]
    },
    []
  )

  const generateCommunityInvoicesTable = useCallback(
    (
      data: TAdminDocumentCommunityInvoiceData['invoices'],
      isFromGPO?: boolean
    ): ContentTable[] => [
      {
        table: {
          headerRows: 1,
          dontBreakRows: true,
          widths: ['*', 80],
          body: [
            [
              {
                text: isFromGPO
                  ? `${DEFAULT_PDF_DOC_TXT.HEALTH_SYSTEM}/${DEFAULT_PDF_DOC_TXT.HOSPITAL}`
                  : DEFAULT_PDF_DOC_TXT.HOSPITAL,
                style: 'tableHeader'
              },
              {
                text: DEFAULT_PDF_DOC_TXT.ADMIN_FEE_SPLIT,
                style: 'tableHeaderRightAlignment'
              }
            ].map((item) => ({ ...item, text: item.text.toUpperCase() })),
            ...data.map((item) => [
              isFromGPO ? item.health_system_name : item.hospital_name,
              {
                text: `$${currencyFormatter(item.admin_fee)}`,
                alignment: 'right'
              }
            ])
          ]
        },
        layout: tableLayout,
        margin: [0, 6, 0, 10]
      }
    ],
    []
  )

  const generateCommunityRemittanceAdviceTable = useCallback(
    (
      data: TAdminDocumentCommunityRemittanceAdviceInvoiceData[],
      fromCOToGPO?: boolean
    ): ContentTable[] => {
      const tableHeader = fromCOToGPO
        ? [
            {
              text: `${DEFAULT_PDF_DOC_TXT.INVOICE} #`,
              style: 'tableHeader'
            },
            { text: DEFAULT_PDF_DOC_TXT.HEALTH_SYSTEM, style: 'tableHeader' },
            {
              text: DEFAULT_PDF_DOC_TXT.CONTRACT_CAT,
              style: 'tableHeader'
            },
            {
              text: DEFAULT_PDF_DOC_TXT.ADMIN_FEE_SPLIT,
              style: 'tableHeaderRightAlignment'
            }
          ]
        : [
            { text: DEFAULT_PDF_DOC_TXT.CONTRACT_CAT, style: 'tableHeader' },
            {
              text: DEFAULT_PDF_DOC_TXT.ADMIN_FEE,
              style: 'tableHeaderRightAlignment'
            }
          ]

      const tableData = data.map((item) =>
        fromCOToGPO
          ? [
              item.supplier_invoice_number,
              item.health_system_name,
              item.contract_category_name,
              {
                text: `$${currencyFormatter(item.admin_fee)}`,
                alignment: 'right'
              }
            ]
          : [
              item.contract_category_name,
              {
                text: `$${currencyFormatter(item.admin_fee)}`,
                alignment: 'right'
              }
            ]
      )

      return [
        {
          table: {
            headerRows: 1,
            dontBreakRows: true,
            widths: tableHeader.length === 4 ? [55, 120, '*', 80] : ['*', 80],
            body: [
              tableHeader.map((item) => ({
                ...item,
                text: item.text.toUpperCase()
              })),
              ...tableData
            ]
          },
          layout: tableLayout,
          margin: [0, 6, 0, 10]
        }
      ]
    },
    []
  )

  const generateHSRemittanceAdviceTable = useCallback(
    (
      data: TAdminDocumentHSRemittanceAdviceInvoiceData[],
      fromHSToGPO?: boolean
    ): ContentTable[] => {
      const tableHeader = fromHSToGPO
        ? [
            {
              text: `${DEFAULT_PDF_DOC_TXT.INVOICE} #`,
              style: 'tableHeader'
            },
            { text: DEFAULT_PDF_DOC_TXT.HOSPITAL, style: 'tableHeader' },
            {
              text: DEFAULT_PDF_DOC_TXT.CONTRACT_CAT,
              style: 'tableHeader'
            },
            {
              text: DEFAULT_PDF_DOC_TXT.ADMIN_FEE_SPLIT,
              style: 'tableHeaderRightAlignment'
            }
          ]
        : [
            { text: DEFAULT_PDF_DOC_TXT.HOSPITAL, style: 'tableHeader' },
            {
              text: DEFAULT_PDF_DOC_TXT.ADMIN_FEE_SPLIT,
              style: 'tableHeaderRightAlignment'
            }
          ]

      const tableData = data.map((item) =>
        fromHSToGPO
          ? [
              item.supplier_invoice_number,
              item.hospital_name ?? '',
              item.contract_category_name,
              {
                text: `$${currencyFormatter(item.admin_fee)}`,
                alignment: 'right'
              }
            ]
          : [
              item.hospital_name ?? '',
              {
                text: `$${currencyFormatter(item.admin_fee)}`,
                alignment: 'right'
              }
            ]
      )

      return [
        {
          table: {
            headerRows: 1,
            dontBreakRows: true,
            widths: tableHeader.length === 4 ? [55, 120, '*', 80] : ['*', 80],
            body: [
              tableHeader.map((item) => ({
                ...item,
                text: item.text.toUpperCase()
              })),
              ...tableData
            ]
          },
          layout: tableLayout,
          margin: [0, 6, 0, 10]
        }
      ]
    },
    []
  )
  const generateVendorInvoicesTable = useCallback(
    (data: TVendorInvoiceData[]): ContentTable[] => [
      {
        table: {
          headerRows: 1,
          dontBreakRows: true,
          widths: ['*', 80],
          body: [
            [
              { text: DEFAULT_PDF_DOC_TXT.CONTRACT_CAT, style: 'tableHeader' },
              {
                text: DEFAULT_PDF_DOC_TXT.ADMIN_FEE,
                style: 'tableHeaderRightAlignment'
              }
            ].map((item) => ({ ...item, text: item.text.toUpperCase() })),
            ...data.map((item) => [
              item.contract_category_name,
              {
                text: `$${currencyFormatter(item.admin_fee)}`,
                alignment: 'right'
              }
            ])
          ]
        },
        layout: tableLayout,
        margin: [0, 6, 0, 10]
      }
    ],
    []
  )

  const generateGPOInvoicesTable = useCallback(
    (data: TGPOInvoiceData[], isFromHS?: boolean): ContentTable[] => [
      {
        table: {
          headerRows: 1,
          dontBreakRows: true,
          widths: [55, 120, '*', 80],
          body: [
            [
              {
                text: `${DEFAULT_PDF_DOC_TXT.INVOICE} #`,
                style: 'tableHeader'
              },
              {
                text: isFromHS
                  ? DEFAULT_PDF_DOC_TXT.HOSPITAL
                  : DEFAULT_PDF_DOC_TXT.HEALTH_SYSTEM,
                style: 'tableHeader'
              },
              {
                text: DEFAULT_PDF_DOC_TXT.CONTRACT_CAT,
                style: 'tableHeader'
              },
              {
                text: DEFAULT_PDF_DOC_TXT.ADMIN_FEE_SPLIT,
                style: 'tableHeaderRightAlignment'
              }
            ].map((item) => ({ ...item, text: item.text.toUpperCase() })),
            ...data.map((item) => [
              item.supplier_invoice_number,
              isFromHS ? item.hospital_name : item.health_system_name,
              item.contract_category_name,
              {
                text: `$${currencyFormatter(item.admin_fee)}`,
                alignment: 'right'
              }
            ])
          ]
        },
        layout: tableLayout,
        margin: [0, 6, 0, 10]
      }
    ],
    []
  )

  const generateGPORemittanceTable = useCallback(
    (data: TGPOInvoiceData[], isFromGPOLedByGPO?: boolean): ContentTable[] => [
      {
        table: {
          headerRows: 1,
          dontBreakRows: true,
          widths: ['*', 80],
          body: [
            [
              {
                text: DEFAULT_PDF_DOC_TXT.CONTRACT_CAT,
                style: 'tableHeader'
              },
              {
                text: isFromGPOLedByGPO
                  ? DEFAULT_PDF_DOC_TXT.ADMIN_FEE
                  : DEFAULT_PDF_DOC_TXT.ADMIN_FEE_SPLIT,
                style: 'tableHeaderRightAlignment'
              }
            ].map((item) => ({ ...item, text: item.text.toUpperCase() })),
            ...data.map((item) => [
              item.contract_category_name,
              {
                text: `$${currencyFormatter(item.admin_fee)}`,
                alignment: 'right'
              }
            ])
          ]
        },
        layout: tableLayout,
        margin: [0, 6, 0, 10]
      }
    ],
    []
  )

  const generateCommonsAdminRemittanceAdviceTable = useCallback(
    (data: TAdminDocumentCommonsRemittanceAdviceInvoiceData[]): Content[] => [
      {
        text: DEFAULT_PDF_DOC_TXT.SUMMARY_BY_CC,
        style: 'table-title'
      },
      {
        table: {
          headerRows: 1,
          dontBreakRows: true,
          widths: [55, '*', 80, 80],
          body: [
            [
              {
                text: `${DEFAULT_PDF_DOC_TXT.INVOICE} #`,
                style: 'tableHeader'
              },
              { text: DEFAULT_PDF_DOC_TXT.CONTRACT_CAT, style: 'tableHeader' },
              {
                text: DEFAULT_PDF_DOC_TXT.ADMIN_FEE,
                style: 'tableHeaderRightAlignment'
              },
              {
                text: DEFAULT_PDF_DOC_TXT.ADMIN_FEE_SPLIT,
                style: 'tableHeaderRightAlignment'
              }
            ].map((item) => ({ ...item, text: item.text.toUpperCase() })),
            ...data.map((item) => [
              item.supplier_invoice_number,
              item.contract_category_name,
              {
                text: `$${currencyFormatter(item.admin_fee)}`,
                alignment: 'right'
              },
              {
                text: `$${currencyFormatter(item.admin_fee_split)}`,
                alignment: 'right'
              }
            ])
          ]
        },
        layout: tableLayout,
        margin: [0, 6, 0, 10]
      }
    ],
    []
  )

  const launchDocGeneration = useCallback(
    (
      content: TDocumentDefinitions['content'],
      filename: string,
      callback?: () => void
    ) => {
      const completeDocTemplate: TDocumentDefinitions = {
        ...defaultA4DocValues,
        footer: generatePaginatedFooterUpdatedUI(),
        content,
        styles: defaultFinancialsDocStyles
      }
      generatePDFDoc(completeDocTemplate, filename, callback)
    },
    []
  )

  const generateRebateInvoiceTable = useCallback(
    (data: TFinancialsDocumentInvoiceData[]): ContentTable[] => [
      {
        table: {
          headerRows: 1,
          widths: [55, '*', 80, 80],
          body: [
            [
              {
                text: `${DEFAULT_PDF_DOC_TXT.INVOICE} #`,
                style: 'tableHeader'
              },
              { text: DEFAULT_PDF_DOC_TXT.CONTRACT_CAT, style: 'tableHeader' },
              {
                text: DEFAULT_PDF_DOC_TXT.INVOICE_AMT,
                style: 'tableHeaderRightAlignment'
              },
              {
                text: DEFAULT_PDF_DOC_TXT.REBATE_AMT,
                style: 'tableHeaderRightAlignment'
              }
            ],
            ...data.map((item) => [
              item.supplier_invoice_number,
              item.contract_category_name,
              {
                text: `$${currencyFormatter(item.invoice_amount)}`,
                alignment: 'right'
              },
              {
                text: `$${currencyFormatter(item.rebate_amount)}`,
                alignment: 'right'
              }
            ])
          ]
        },
        layout: tableLayout,
        margin: [0, 6, 0, 10]
      }
    ],
    []
  )

  const generateCognusARInvoiceTable = useCallback(
    (data: TCognusARDocumentInvoiceData[]): ContentTable[] => [
      {
        table: {
          headerRows: 1,
          widths: [55, 55, '*', 80, 80],
          body: [
            [
              {
                text: `${DEFAULT_PDF_DOC_TXT.INVOICE} #`,
                style: 'tableHeader'
              },
              { text: DEFAULT_PDF_DOC_TXT.HOSPITAL, style: 'tableHeader' },
              { text: DEFAULT_PDF_DOC_TXT.CONTRACT_CAT, style: 'tableHeader' },
              {
                text: DEFAULT_PDF_DOC_TXT.INVOICE_AMT,
                style: 'tableHeaderRightAlignment'
              },
              {
                text: DEFAULT_PDF_DOC_TXT.OM_COMMUNITY_FEE,
                style: 'tableHeaderRightAlignment'
              }
            ],
            ...data.map((item) => [
              item.supplier_invoice_number,
              item.hospital_name,
              item.contract_category_name,
              {
                text: `$${currencyFormatter(item.invoice_amount)}`,
                alignment: 'right'
              },
              {
                text: `$${currencyFormatter(item.cognus_amount)}`,
                alignment: 'right'
              }
            ])
          ]
        },
        layout: tableLayout,
        margin: [0, 6, 0, 10]
      }
    ],
    []
  )

  return {
    getPdfFileName,
    generateFromToSection,
    generateDocumentInfo,
    generateCommunityRemittanceAdviceTable,
    generateCommonsAdminRemittanceAdviceTable,
    generateCommentsAndTotalSection,
    generateSummaryTable,
    generateVendorInvoicesTable,
    generateCommunityInvoicesTable,
    launchDocGeneration,
    generateGPOInvoicesTable,
    generateGPORemittanceTable,
    generateHSRemittanceAdviceTable,
    generateRebateInvoiceTable,
    generateCognusARInvoiceTable
  }
}
